import React, { useEffect, useMemo, useState } from 'react';
import { getInvoices, deleteAdminInvoice } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { FileText, Eye, Search, Calendar, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import usePageTitle from '../hooks/usePageTitle';
import InvoicePopup from '../components/sessions/InvoicePopup';
import { generateInvoiceNumber } from '../components/sessions/ChargingInvoiceDocument';

const formatEuropeanDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return '—';
  }
};

const isTopupInvoice = (invoice) => {
  const mergedType = `${String(invoice?.type || '').toLowerCase()} ${String(invoice?.invoice_type || '').toLowerCase()}`;
  return (
    mergedType.includes('topup') ||
    mergedType.includes('recharge') ||
    mergedType.includes('business_credit') ||
    mergedType.includes('business_employee_allocation')
  );
};

const getInvoiceCategory = (invoice) => (isTopupInvoice(invoice) ? 'topup' : 'charging');

const getInvoiceTypeLabel = (invoice) => {
  if (isTopupInvoice(invoice)) {
    const mergedType = `${String(invoice?.type || '').toLowerCase()} ${String(invoice?.invoice_type || '').toLowerCase()}`;
    if (mergedType.includes('cash') || mergedType.includes('business_credit') || mergedType.includes('business_employee_allocation')) return 'Rimbushje Cash';
    if (mergedType.includes('online') || mergedType.includes('card')) return 'Blerje kreditesh';
    return 'Rimbushje';
  }
  return 'Karikim';
};

const isCashTopupInvoice = (invoice) => {
  const mergedType = `${String(invoice?.type || '').toLowerCase()} ${String(invoice?.invoice_type || '').toLowerCase()}`;
  const providerRaw = `${String(invoice?.payment_provider || '').toLowerCase()} ${String(invoice?.processor || '').toLowerCase()} ${String(invoice?.payment_method || '').toLowerCase()}`;
  return mergedType.includes('cash_topup') || providerRaw.includes('cash');
};

const isPokTopupInvoice = (invoice) => {
  if (!isTopupInvoice(invoice) || isCashTopupInvoice(invoice)) return false;

  const providerRaw = `${String(invoice?.payment_provider || '').toLowerCase()} ${String(invoice?.processor || '').toLowerCase()} ${String(invoice?.payment_method || '').toLowerCase()}`;
  const description = String(invoice?.description || '').toLowerCase();
  const fee = Number(invoice?.fee ?? 0);
  const total = Number(invoice?.total ?? 0);
  const amount = Number(invoice?.amount ?? invoice?.credited_amount ?? 0);

  return (
    providerRaw.includes('pok') ||
    description.includes('pok') ||
    fee > 0 ||
    total > amount
  );
};

const getInvoiceAmount = (invoice) => {
  if (isPokTopupInvoice(invoice)) {
    const amount = Number(invoice?.amount ?? invoice?.credited_amount ?? 0);
    const fee = Number(invoice?.fee ?? 0);
    const total = Number(invoice?.total ?? 0);
    if (total > 0) return total;
    if (amount > 0) return amount + fee;
  }

  return Number(invoice?.amount ?? invoice?.cost_lek ?? 0);
};

const getInvoiceDate = (invoice) =>
  invoice?.created_at || invoice?.paid_at || invoice?.end_time || invoice?.start_time;

const getInvoiceNumberText = (invoice) => invoice?.invoice_number || generateInvoiceNumber(invoice);

const getInvoiceClientLabel = (invoice) =>
  invoice?.client_name || invoice?.user_name || invoice?.client_email || invoice?.user_email || 'N/A';

const getInvoiceLocation = (invoice) => {
  const site = invoice?.site_name;
  const charger = invoice?.charger_id || invoice?.charger_name;
  if (site && charger) return `${site} / ${charger}`;
  return site || charger || '—';
};

const matchesSearch = (invoice, normalizedSearch) => {
  if (!normalizedSearch) return true;

  const fields = [
    getInvoiceNumberText(invoice),
    invoice?.type,
    getInvoiceTypeLabel(invoice),
    invoice?.client_name,
    invoice?.client_email,
    invoice?.user_name,
    invoice?.user_email,
    invoice?.site_name,
    invoice?.charger_id,
    invoice?.charger_name,
    invoice?.description,
  ];

  return fields.some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
};

const Invoices = () => {
  usePageTitle('Faturat');
  const { isAdmin } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await getInvoices({ skip: 0, limit: 500 });
      const payload = response?.data;
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

      items.sort((a, b) => {
        const da = new Date(getInvoiceDate(a) || 0).getTime();
        const db = new Date(getInvoiceDate(b) || 0).getTime();
        return db - da;
      });

      setInvoices(items);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Gabim në ngarkimin e faturave');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const filteredInvoices = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return invoices.filter((invoice) => {
      if (typeFilter !== 'all' && getInvoiceCategory(invoice) !== typeFilter) {
        return false;
      }
      return matchesSearch(invoice, normalizedSearch);
    });
  }, [invoices, searchTerm, typeFilter]);

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoicePreviewOpen(true);
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!invoice?.id) {
      toast.error('Fatura nuk është e vlefshme për fshirje');
      return;
    }

    const confirmed = window.confirm('A je i sigurt që dëshiron të fshish këtë faturë?');
    if (!confirmed) return;

    try {
      await deleteAdminInvoice(invoice.id);
      setInvoices((prev) => prev.filter((item) => item.id !== invoice.id));
      toast.success('Fatura u fshi me sukses');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error?.response?.data?.detail || 'Gabim gjatë fshirjes së faturës');
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-outfit">Faturat</h1>
            <p className="text-zinc-400 text-sm">Menaxhoni faturat e rimbushjes dhe karikimit</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchInvoices} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Kërko sipas nr. fature, klienti, stacioni..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-zinc-800/50"
            data-testid="invoice-search-input"
          />
        </div>

        <div className="w-full md:w-64">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-zinc-800/50 border-white/10 text-white">
              <SelectValue placeholder="Filtro sipas tipit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha</SelectItem>
              <SelectItem value="topup">Vetëm Rimbushje</SelectItem>
              <SelectItem value="charging">Vetëm Karikime</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-outfit flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            Lista e Faturave ({filteredInvoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Nr. Faturës</TableHead>
                  <TableHead className="text-zinc-400">Tipi</TableHead>
                  <TableHead className="text-zinc-400">Klienti</TableHead>
                  <TableHead className="text-zinc-400">Data</TableHead>
                  <TableHead className="text-zinc-400">Stacion/Karikues</TableHead>
                  <TableHead className="text-zinc-400">Totali</TableHead>
                  <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-zinc-500 py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                      <p>Nuk ka fatura për filtrat aktualë</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <code className="text-xs bg-zinc-800 px-2 py-1 rounded text-emerald-400">
                          {getInvoiceNumberText(invoice)}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={isTopupInvoice(invoice)
                            ? 'border-amber-400/40 text-amber-300'
                            : 'border-blue-400/40 text-blue-300'}
                        >
                          {getInvoiceTypeLabel(invoice)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-white">{getInvoiceClientLabel(invoice)}</p>
                          <p className="text-xs text-zinc-500">{invoice.client_email || invoice.user_email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-white" />
                          {formatEuropeanDate(getInvoiceDate(invoice))}
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {getInvoiceLocation(invoice)}
                      </TableCell>
                      <TableCell className="font-semibold text-emerald-400">
                        {formatCurrency(getInvoiceAmount(invoice), isTopupInvoice(invoice) ? 'LEK' : 'Kredite')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewInvoice(invoice)}
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Shiko
                          </Button>
                          {isAdmin() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteInvoice(invoice)}
                              data-testid={`delete-invoice-${invoice.id}`}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Fshi
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <InvoicePopup
        session={invoicePreviewOpen ? selectedInvoice : null}
        onClose={() => {
          setInvoicePreviewOpen(false);
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
};

export default Invoices;
